import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import translations from '../../content/PagesContent/AdminPages/adminPagesTranslations.json';
// Компоненты
import '../../styles/AdminDashboard.css';
import { getAdminConfig } from './entitiesConfig';
import EntitySelector from './EntitySelector';
import EntityFormModal from './EntityFormModal';
import EntityTable from './EntityTable';
import useTranslations from '../../customHook/useTranslations';

/**
 * Главный компонент админ-панели.
 * Управляет состоянием выбранной сущности, модального окна, а также логикой добавления и редактирования записей.
 */
function AdminDashboard() {
  const dispatch = useDispatch();

  // получение словаря для статических данных в зависимости от выбранного языка
  const t = useTranslations(translations);

  const entitiesConfig = useMemo(() => getAdminConfig(t), [t]);

  // Состояние выбранной сущности (дороги, дирекции и т.д.)
  const [selectedEntity, setSelectedEntity] = useState('roads');

  //Состояние режима работы с сущностью (добавление, редактирование, удаление)
  const [entityMode, setEntityMode] = useState(); // 'add' | 'edit' | 'delete'

  // Конфигурация текущей выбранной сущности из entitiesConfig
  const entityConfig = entitiesConfig[selectedEntity];

  // Состояние открытия/закрытия модального окна формы
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Состояние редактируемого элемента (null - если добавляем новую запись)
  const [editingItem, setEditingItem] = useState(null);

  // Состояние удаляемого элемента 
  const [deletingItem, setDeletingItem] = useState(null);

  /**
   * Обработчик выбора новой сущности в селекторе, отправляет запрос за данными
   * @param {string} entityKey - ключ выбранной сущности (например, 'roads')
   */
  const handleSelect = (entityKey) => {
    setSelectedEntity(entityKey);
    const getAction = entitiesConfig[entityKey]?.actions?.get;
    if (getAction) {
      dispatch(getAction());
    }
  };
  /**
   * Обработчик добавления элемента
   */
  const handleAddNew = (data) => {
    setEditingItem(data || null); // если data передано, то используем его, иначе null
    setEntityMode('add');
    setIsModalOpen(true);
  };

  /**
   * Обработчик редактирования элемента (передаёт его в форму)
   * @param {Object} item - редактируемая запись
   */
  const handleEdit = (item) => {
    setEditingItem(item);
    setEntityMode('edit');
    setIsModalOpen(true);
  };

  /**
   * Обработчик удаления элемента (передаёт его в форму)
   * @param {Object} item - удаляемая запись
   */
  const handleDelete = (item) => {
    setDeletingItem(item);
    setEntityMode('delete');
    setIsModalOpen(true);
  };

  // Закрытие модального окна и очистка состояния редактируемого элемента
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  useEffect(() => {
    handleSelect('roads');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="admin-dashboard">
      {/* Селектор сущностей (дороги, дирекции и т.д.) */}
      <EntitySelector selectedEntity={selectedEntity} onSelectEntity={handleSelect} entities={entitiesConfig} t={t} />

      {/* Таблица с данными выбранной сущности */}
      <EntityTable
        entityKey={selectedEntity}
        entityConfig={entityConfig}
        onAddNew={handleAddNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
        t={t}
      />

      {/* Модалка для добавления/редактирования записи/ удаления */}
      {isModalOpen && (
        <EntityFormModal
          entityKey={selectedEntity}
          config={entityConfig}
          initialData={editingItem}
          entityMode={entityMode}
          onClose={closeModal}
          deletingItem={deletingItem}
          t={t}
        />
      )}
    </section>
  );
}

export default AdminDashboard;
